"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/* Defers mounting a subtree until it scrolls near the viewport.
 *
 * Next's `dynamic()` code-SPLITS a component but still requests its chunk as
 * soon as the parent renders, so a heavy below-the-fold widget (the API
 * section's syntax highlighter pulls ~500KB of Prism) still lands in the
 * initial page load and competes with the hero for bandwidth. Not rendering it
 * at all until it is close to view is what actually keeps the chunk out of the
 * critical path.
 *
 * Only use this for content that is already `ssr: false` or purely decorative:
 * anything a crawler should read must stay in the server-rendered HTML.
 * `placeholder` should reserve the same height as the real content, otherwise
 * mounting it shifts the page (CLS).
 */

interface Props {
  children: ReactNode;
  /** Shown until the block scrolls near view. Give it the real content's height. */
  placeholder?: ReactNode;
  /** How far ahead of the viewport to mount. */
  rootMargin?: string;
}

export function LazyMount({ children, placeholder = null, rootMargin = "400px" }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return;
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (very old browsers, some test envs): render now
    // rather than leaving the placeholder up forever.
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, rootMargin]);

  return <div ref={ref}>{shown ? children : placeholder}</div>;
}
